import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

type GtagParams = Record<string, string | number | boolean>;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly measurementId = environment.googleAnalytics?.measurementId ?? '';
  private activo = false;

  /** Inicializa GA4: stub dataLayer, config, suscripción al router y carga async de gtag.js. */
  init(): void {
    if (!this.debeActivarse()) {
      return;
    }

    this.activo = true;
    this.inicializarDataLayer();
    this.configurarGtag();
    this.setUserProperties({
      app_version: environment.appVersion,
      platform: Capacitor.getPlatform(),
    });
    this.suscribirNavegacion();
    void this.cargarScript();
  }

  /** Registra un page view manual (SPA). */
  trackPageView(path: string, title?: string): void {
    if (!this.activo) {
      return;
    }

    const pagePath = path || '/';
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: title ?? document.title,
      page_location: `${window.location.origin}${pagePath}`,
    });
  }

  /** Evento custom de GA4. No incluir PII (RUT, email, nombres, IDs de usuario). */
  trackEvent(name: string, params?: GtagParams): void {
    if (!this.activo) {
      return;
    }

    window.gtag('event', name, params ?? {});
  }

  /** Propiedades de usuario agregadas (p. ej. id_perfil, modo_offline). Sin PII. */
  setUserProperties(props: GtagParams): void {
    if (!this.activo) {
      return;
    }

    window.gtag('set', 'user_properties', props);
  }

  private debeActivarse(): boolean {
    if (!environment.googleAnalytics?.enabled || !this.measurementId) {
      return false;
    }
    if (typeof window === 'undefined') {
      return false;
    }

    const host = window.location.hostname;
    return host !== 'localhost' && host !== '127.0.0.1' && host !== '::1';
  }

  private inicializarDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
  }

  private configurarGtag(): void {
    window.gtag('config', this.measurementId, {
      send_page_view: false,
    });
  }

  private suscribirNavegacion(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects ?? event.url ?? '/';
        const path = url.split('#')[0] || '/';
        this.trackPageView(path);
      });
  }

  private cargarScript(): Promise<void> {
    const src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    const existente = document.querySelector(`script[src="${src}"]`);
    if (existente) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('No se pudo cargar gtag.js'));
      document.head.appendChild(script);
    });
  }
}
